/**
 * ITT-972: Engage callables for beer profile processing failures (1a).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import {
  CALLABLE_BEER_LIST_PROFILE_FAILURES,
  CALLABLE_BEER_RETRY_PROFILE_FAILURE,
  CALLABLE_BEER_UPDATE_PROFILE_FAILURE_DISP,
} from '../utils/callableBeerConstants';

function parseIsoDate(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeCountMap(rawMap) {
  if (!rawMap || typeof rawMap !== 'object') {
    return {};
  }
  const normalized = {};
  Object.entries(rawMap).forEach(([key, value]) => {
    normalized[key] = Number(value) || 0;
  });
  return normalized;
}

export function normalizeProfileFailureSummary(rawSummary) {
  if (!rawSummary || typeof rawSummary !== 'object') {
    return {
      open: 0,
      resolved: 0,
      byCategory: {},
      byStage: {},
      reportingAsOf: null,
      reportingStale: false,
    };
  }
  return {
    open: Number(rawSummary.open) || 0,
    resolved: Number(rawSummary.resolved) || 0,
    byCategory: normalizeCountMap(rawSummary.byCategory),
    byStage: normalizeCountMap(rawSummary.byStage),
    reportingAsOf: parseIsoDate(rawSummary.reportingAsOf),
    reportingStale: rawSummary.reportingStale === true,
  };
}

export function normalizeProfileFailureItem(rawItem = {}) {
  return {
    ...rawItem,
    itemId: rawItem.itemId || rawItem.id || rawItem.itemKey || '',
    itemKey: rawItem.itemKey || rawItem.id || rawItem.itemId || '',
    openedAt: parseIsoDate(rawItem.openedAt),
    updatedAt: parseIsoDate(rawItem.updatedAt),
    resolvedAt: parseIsoDate(rawItem.resolvedAt),
    dispositionAt: parseIsoDate(rawItem.dispositionAt),
    retriedAt: parseIsoDate(rawItem.retriedAt),
  };
}

/**
 * @param {{
 *   disposition?: 'open'|'resolved',
 *   category?: string,
 *   failureStage?: string,
 *   breweryId?: string,
 *   beerId?: string,
 *   runId?: string,
 *   limit?: number,
 * }} [options]
 */
export async function listProfileProcessingFailures(options = {}) {
  const listFn = httpsCallable(functions, CALLABLE_BEER_LIST_PROFILE_FAILURES);
  const payload = {
    disposition: options.disposition ?? 'open',
  };
  if (options.category) {
    payload.category = options.category;
  }
  if (options.failureStage) {
    payload.failureStage = options.failureStage;
  }
  if (options.breweryId) {
    payload.breweryId = options.breweryId;
  }
  if (options.beerId) {
    payload.beerId = options.beerId;
  }
  if (options.runId) {
    payload.runId = options.runId;
  }
  if (typeof options.limit === 'number') {
    payload.limit = options.limit;
  }
  const result = await listFn(payload);
  const data = result.data ?? {};
  const rows = Array.isArray(data.rows)
    ? data.rows.map(normalizeProfileFailureItem)
    : [];
  return {
    rows,
    summary: normalizeProfileFailureSummary(data.summary),
  };
}

export async function resolveProfileProcessingFailure(itemId) {
  const updateFn = httpsCallable(functions, CALLABLE_BEER_UPDATE_PROFILE_FAILURE_DISP);
  const result = await updateFn({ itemId, disposition: 'resolved' });
  const data = result.data ?? {};
  return normalizeProfileFailureItem(data.item);
}

export async function retryProfileProcessingFailure(itemId) {
  const retryFn = httpsCallable(functions, CALLABLE_BEER_RETRY_PROFILE_FAILURE, {
    timeout: 120000,
  });
  const result = await retryFn({ itemId });
  const data = result.data ?? {};
  return {
    item: normalizeProfileFailureItem(data.item),
    beerId: data.beerId ?? null,
    runId: data.runId ?? null,
    candidateKey: data.candidateKey ?? null,
  };
}
